import { cloneDeep } from "lodash";

import types from "./types";
import { resolveEcmSectionLabelFromUpgradePayload } from "utils/packageUpgradeEcmCategories";

const initialState = {
    parametricAnalysis: null,
    parametricAnalysisToCompare: null,
    isParametricAnalysisLoading: true,
    isParametricAnalysisCreating: false,
    isSavingEcmInputMap: false,
    isSavingParametricAnalysis: false,
    fetchParametricAnalysisError: null,
};

const parametricAnalysisReducer = (state = initialState, action) => {
    switch (action.type) {
        case types.CREATE_PARAMETRIC_ANALYSIS_START:
            return { ...state, isParametricAnalysisCreating: true, fetchParametricAnalysisError: null };
        case types.CREATE_PARAMETRIC_ANALYSIS_SUCCESS:
            return { ...state, isParametricAnalysisCreating: false };
        case types.CREATE_PARAMETRIC_ANALYSIS_ERROR:
            return { ...state, isParametricAnalysisCreating: false };
        case types.FETCH_PARAMETRIC_ANALYSIS_START:
            return { ...state, isParametricAnalysisLoading: true, fetchParametricAnalysisError: null };
        case types.SYNC_PARAMETRIC_ANALYSIS_SUCCESS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ...action.payload,
                },
                parametricAnalysisToCompare: cloneDeep({ ...state.parametricAnalysis, ...action.payload }),
                isParametricAnalysisLoading: false,
                fetchParametricAnalysisError: null,
            };
        case types.FETCH_PARAMETRIC_ANALYSIS_SUCCESS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ...action.payload,
                },
                parametricAnalysisToCompare: cloneDeep({ ...state.parametricAnalysis, ...action.payload }),
                isParametricAnalysisLoading: false,
                fetchParametricAnalysisError: null,
            };
        case types.FETCH_PARAMETRIC_ANALYSIS_ERROR:
            return {
                ...state,
                isParametricAnalysisLoading: false,
                fetchParametricAnalysisError: action.payload,
                parametricAnalysis: null,
            };
        case types.SAVE_ECM_INPUT_MAP_START:
            return { ...state, isSavingEcmInputMap: true };
        case types.SAVE_ECM_INPUT_MAP_SUCCESS:
            return {
                ...state,
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    ecmInputMap: cloneDeep(state.parametricAnalysis.ecmInputMap),
                },
                isSavingEcmInputMap: false,
            };
        case types.SAVE_ECM_INPUT_MAP_ERROR:
            return { ...state, isSavingEcmInputMap: false };
        case types.CLEAR_PARAMETRIC_ANALYSIS:
            return { ...initialState };
        case types.ADD_UPGRADE_TO_PARAMETRIC_ANALYSIS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ecmInputMap: {
                        ...state.parametricAnalysis.ecmInputMap,
                        [action.payload.sectionType]: {
                            ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType],
                            ...action.payload.upgrade,
                        },
                    },
                },
            };
        case types.TOGGLE_UPGRADE_IN_PARAMETRIC_ANALYSIS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ecmInputMap: {
                        ...state.parametricAnalysis.ecmInputMap,
                        [action.payload.sectionType]: {
                            ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType],
                            [action.payload.upgradeId]: {
                                ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType][
                                    action.payload.upgradeId
                                ],
                                active: action.payload.toggleValue,
                            },
                        },
                    },
                },
            };
        case types.REMOVE_UPGRADE_FROM_PARAMETRIC_ANALYSIS: {
            const { sectionType, upgradeId } = action.payload;

            const section = {
                ...state.parametricAnalysis.ecmInputMap[sectionType],
            };

            delete section[upgradeId];

            const ecmInputMap = {
                ...state.parametricAnalysis.ecmInputMap,
                [sectionType]: section,
            };

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ecmInputMap,
                },
            };
        }
        case types.UPDATE_ECM_INPUT_MAP:
            return {
                ...state,
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    ecmInputMap: cloneDeep(action.payload),
                },
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ecmInputMap: action.payload,
                },
            };
        case types.UPDATE_ECM_INPUT_MAP_UPGRADE:
            const { sectionType, upgradeId, upgrade, areNewPackageValues } = action.payload;

            const fullUpgrade =
                state.parametricAnalysis.ecmInputMap[action.payload.sectionType]?.[action.payload.upgradeId] || {};

            const { firstBatchId } = fullUpgrade;

            let passedAndIndividualSavings = {
                passed: areNewPackageValues
                    ? fullUpgrade?.passed === false
                        ? false
                        : null
                    : fullUpgrade?.passed
                      ? fullUpgrade?.passed
                      : null,
                individualSavings: areNewPackageValues
                    ? null
                    : fullUpgrade?.individualSavings
                      ? fullUpgrade?.individualSavings
                      : null,
            };

            if (firstBatchId) {
                passedAndIndividualSavings = {
                    passed: fullUpgrade?.passed || null,
                    individualSavings: fullUpgrade?.individualSavings || null,
                };
            }

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ecmInputMap: {
                        ...state.parametricAnalysis.ecmInputMap,
                        [sectionType]: {
                            ...state.parametricAnalysis.ecmInputMap[sectionType],
                            [upgradeId]: {
                                ...(state.parametricAnalysis.ecmInputMap[sectionType]?.[upgradeId] || {}),
                                label: resolveEcmSectionLabelFromUpgradePayload(sectionType, upgrade),
                                ...passedAndIndividualSavings,
                                packageValues: {
                                    ...(state.parametricAnalysis.ecmInputMap[sectionType]?.[upgradeId]?.packageValues ||
                                        {}),
                                    ...upgrade,
                                },
                            },
                        },
                    },
                },
            };
        case types.UPDATE_VALIDATION_PRE_CHECK_RUN_STATUS:
            const parametricDetails = state?.parametricAnalysis?.parametricDetails || {};
            const parametricDetailsToCompare = state?.parametricAnalysisToCompare?.parametricDetails || {};

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    parametricDetails: {
                        ...parametricDetails,
                        ...action.payload,
                    },
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    parametricDetails: {
                        ...parametricDetailsToCompare,
                        ...action.payload,
                    },
                },
            };
        case types.DUPLICATE_PARAMETRIC_ANALYSIS_START:
            return { ...state, isParametricAnalysisLoading: true };
        case types.DUPLICATE_PARAMETRIC_ANALYSIS_SUCCESS:
            return { ...state, isParametricAnalysisLoading: false };
        case types.DUPLICATE_PARAMETRIC_ANALYSIS_ERROR:
            return { ...state, isParametricAnalysisLoading: false };
        case types.UPDATE_RUN_RESULTS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    runResults: action.payload,
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    runResults: action.payload,
                },
            };
        case types.PIN_UNPIN_RECIPES_START:
            return { ...state };
        case types.PIN_UNPIN_RECIPES_SUCCESS:
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    parametricDetails: { ...state.parametricAnalysis.parametricDetails, pinnedRecipes: action.payload },
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    parametricDetails: { ...state.parametricAnalysis.parametricDetails, pinnedRecipes: action.payload },
                },
            };
        case types.PIN_UNPIN_RECIPES_ERROR:
            return { ...state };
        case types.DIRECT_COST_UPDATE:
            const newValue = action.payload.isBaseCase
                ? {
                      ...state,
                      parametricAnalysis: {
                          ...state.parametricAnalysis,
                          ecmInputMap: {
                              ...state.parametricAnalysis.ecmInputMap,
                              [action.payload.sectionType]: {
                                  ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType],
                                  [action.payload.upgradeId]: {
                                      ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType][
                                          action.payload.upgradeId
                                      ],
                                      cost: action.payload.isBaseCase ? action.payload.newCostData : null,
                                  },
                              },
                          },
                      },
                  }
                : {
                      ...state,
                      parametricAnalysis: {
                          ...state.parametricAnalysis,
                          ecmInputMap: {
                              ...state.parametricAnalysis.ecmInputMap,
                              [action.payload.sectionType]: {
                                  ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType],
                                  [action.payload.upgradeId]: {
                                      ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType][
                                          action.payload.upgradeId
                                      ],
                                      cost: action.payload.isBaseCase ? action.payload.newCostData : null,
                                      packageValues: action.payload.isBaseCase
                                          ? null
                                          : {
                                                ...state.parametricAnalysis.ecmInputMap[action.payload.sectionType][
                                                    action.payload.upgradeId
                                                ].packageValues,
                                                cost: action.payload.newCostData,
                                            },
                                  },
                              },
                          },
                      },
                  };

            return newValue;
        case types.UPDATE_FUEL_COSTS: {
            const { fuelType, name, value } = action.payload;

            const configuration = state.parametricAnalysis?.configuration || {};
            const fuelCosts = state.parametricAnalysis?.configuration?.fuelCosts || {};
            const fuelCostsType = fuelCosts[fuelType] || {};

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    configuration: {
                        ...configuration,
                        fuelCosts: {
                            ...fuelCosts,
                            [fuelType]: {
                                ...fuelCostsType,
                                [name]: value,
                            },
                        },
                    },
                },
            };
        }
        case types.UPDATE_EMISSION_FACTORS: {
            const { fuelType, value } = action.payload;

            const configuration = state.parametricAnalysis?.configuration || {};
            const emissionFactors = state.parametricAnalysis?.configuration?.emissionFactors || {};

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    configuration: {
                        ...configuration,
                        emissionFactors: {
                            ...emissionFactors,
                            [fuelType]: value,
                        },
                    },
                },
            };
        }
        case types.SAVE_PARAMETRIC_CONFIGURATION_START:
        case types.SAVE_PARAMETRIC_DETAILS_START:
            return { ...state, isSavingParametricAnalysis: true };
        case types.SAVE_PARAMETRIC_CONFIGURATION_ERROR:
        case types.SAVE_PARAMETRIC_DETAILS_ERROR:
            return { ...state, isSavingParametricAnalysis: false };
        case types.UPDATE_ERS_REFERENCE_RESULTS: {
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    ersReferenceResults: action.payload,
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    ersReferenceResults: action.payload,
                },
            };
        }
        case types.SAVE_PARAMETRIC_CONFIGURATION_SUCCESS: {
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    configuration: action.payload,
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    configuration: action.payload,
                },
                isSavingParametricAnalysis: false,
            };
        }
        case types.SAVE_PARAMETRIC_DETAILS_SUCCESS: {
            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    parametricDetails: action.payload,
                },
                parametricAnalysisToCompare: {
                    ...state.parametricAnalysisToCompare,
                    parametricDetails: action.payload,
                },
                isSavingParametricAnalysis: false,
            };
        }
        case types.UPDATE_PARAMETRIC_DISPLAY_SETTINGS: {
            const currentDisplaySettings = state.parametricAnalysis?.parametricDetails?.displaySettings || {};

            const { noChangeLabels: currentNoChangeLabels = {} } = currentDisplaySettings;

            const { noChangeLabels: noChangeLabelsPayload = {}, ...rest } = action?.payload || {};

            return {
                ...state,
                parametricAnalysis: {
                    ...state.parametricAnalysis,
                    parametricDetails: {
                        ...state.parametricAnalysis.parametricDetails,
                        displaySettings: {
                            ...currentDisplaySettings,
                            ...rest,
                            noChangeLabels: {
                                ...currentNoChangeLabels,
                                ...noChangeLabelsPayload,
                            },
                        },
                    },
                },
            };
        }
        default:
            return state;
    }
};

export default parametricAnalysisReducer;
